// src/cockpit4/components/AddBrandWizardModal.tsx
// ============================================
// 「+ 增加品牌监测」4 步向导 Modal（图1）
//   ① 添加品牌信息（名称/官网/介绍 + AI填写）
//   ② 监测品牌词（别名/竞品/标签）
//   ③ 监测问题（预设问题池 + 自定义）
//   ④ 监测频次（频率/时间/平台勾选）
// ============================================

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Check, Sparkles, Info } from "lucide-react";

export interface BrandMonitorSpec {
  // Step1
  brandName: string;
  websiteUrl: string;
  intro: string;
  // Step2
  brandWords: string[];
  competitors: string[];
  tags: string[];
  // Step3
  questions: string[];
  // Step4
  frequency: "hourly" | "daily" | "weekly";
  runAt: string; // HH:mm
  platforms: string[]; // 平台key
  enabled: boolean;
}

export const DEFAULT_SPEC = (): BrandMonitorSpec => ({
  brandName: "",
  websiteUrl: "",
  intro: "",
  brandWords: [],
  competitors: [],
  tags: [],
  questions: [],
  frequency: "daily",
  runAt: "09:00",
  platforms: ["doubao", "deepseek", "yuanbao", "qianwen", "baidu", "wenxin", "kimi"],
  enabled: true,
});

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (spec: BrandMonitorSpec) => Promise<void> | void;
  initial?: Partial<BrandMonitorSpec>;
  /** 可选用的预设问题池 */
  presetQuestions?: string[];
  /** 可用平台列表 */
  availablePlatforms?: Array<{ key: string; name: string; color: string; short: string; enabled?: boolean }>;
}

const STEPS = [
  { k: 1, label: "添加品牌信息", desc: "填写品牌基本信息，使用AI自动识别并填写品牌官网与介绍" },
  { k: 2, label: "监测品牌词", desc: "配置品牌别名、竞品词、标签，提升提及识别精度" },
  { k: 3, label: "监测问题", desc: "选择或输入用于 AI 轮询询问的行业问题" },
  { k: 4, label: "监测频次", desc: "设定监测频率、时间与目标 AI 平台" },
];

export default function AddBrandWizardModal({
  open, onClose, onSubmit, initial,
  presetQuestions = [
    "国内有哪些可参考的厂商或产品？",
    "什么是GEO生成式引擎优化？有哪些做得不错的服务商？",
    "有哪些落地案例或方案？",
    "GEO和DSO是什么关系？两者配合怎么帮助企业获客？",
    "有哪些类似的AI企业操作系统？",
    "有哪些权威的行业白皮书或报告？",
  ],
  availablePlatforms = [
    { key: "doubao", name: "豆包", color: "#3B82F6", short: "豆" },
    { key: "deepseek", name: "DeepSeek", color: "#10B981", short: "DS" },
    { key: "yuanbao", name: "元宝", color: "#06B6D4", short: "元" },
    { key: "qianwen", name: "千问", color: "#8B5CF6", short: "千" },
    { key: "baidu", name: "百度AI", color: "#EF4444", short: "百" },
    { key: "wenxin", name: "文心", color: "#F87171", short: "文" },
    { key: "kimi", name: "Kimi", color: "#F59E0B", short: "K" },
    { key: "aidouyin", name: "AI抖音", color: "#EC4899", short: "抖" },
    { key: "chatglm", name: "智谱", color: "#0EA5E9", short: "智" },
    { key: "hunyuan", name: "混元", color: "#14B8A6", short: "混" },
    { key: "yi", name: "零一万物", color: "#6366F1", short: "一" },
    { key: "xingchen", name: "MiniMax", color: "#E11D48", short: "X" },
    { key: "moonshot", name: "月之暗面", color: "#A16207", short: "月" },
  ],
}: Props) {
  const [step, setStep] = useState(1);
  const [spec, setSpec] = useState<BrandMonitorSpec>({ ...DEFAULT_SPEC(), ...(initial || {}) });
  const [aiFilling, setAiFilling] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setSpec({ ...DEFAULT_SPEC(), ...(initial || {}) });
      setAiFilling(false);
      setSubmitting(false);
    }
  }, [open, initial]);

  const tagChips = (items: string[], setItems: (v: string[]) => void, placeholder: string, color: string) => (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[34px]">
        {items.map((t, i) => (
          <span key={i} className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border`}
            style={{ borderColor: `${color}55`, background: `${color}15`, color }}>
            {t}
            <button onClick={() => setItems(items.filter((_, j) => i !== j))}
              className="opacity-60 hover:opacity-100 ml-1">×</button>
          </span>
        ))}
      </div>
      <ChipInput placeholder={placeholder} color={color}
        onAdd={(v) => v && !items.includes(v) && setItems([...items, v])} />
    </div>
  );

  const canNext = useMemo(() => {
    if (step === 1) return !!spec.brandName.trim();
    if (step === 2) return true;
    if (step === 3) return spec.questions.length > 0;
    if (step === 4) return spec.platforms.length > 0;
    return true;
  }, [step, spec]);

  const doAiFill = async () => {
    if (!spec.brandName.trim()) return;
    setAiFilling(true);
    await new Promise((r) => setTimeout(r, 700));
    const name = spec.brandName.trim();
    const lower = name.toLowerCase();
    const auto = {
      websiteUrl: spec.websiteUrl ||
        (lower.includes("http") ? name : `https://www.${lower.replace(/\s+/g, "").replace(/[^a-z0-9]/g, "")}.com/`),
      intro: spec.intro ||
        `${name} 是一家专注于 ${spec.tags[0] || "企业数字化 / AI 操作系统 / GEO+DSO 获客优化"} 领域的科技品牌，主打 ${spec.tags[1] || "品牌可见性优化、权威信源铺设、AI引用追踪"} 能力。详细介绍建议结合官网 About 页面补充，品牌词库与 AI 归因越准确，监测数据越具参考价值。`,
    };
    setSpec((s) => ({
      ...s,
      websiteUrl: auto.websiteUrl,
      intro: auto.intro,
      brandWords: s.brandWords.length ? s.brandWords : [name, `${name}AI`, `${name}科技`],
    }));
    setAiFilling(false);
  };

  const submitAll = async () => {
    if (!canNext) return;
    setSubmitting(true);
    try {
      await onSubmit(spec);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.22 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl text-slate-800 overflow-hidden"
        >
          {/* 顶部渐变 Banner */}
          <div className="relative bg-gradient-to-br from-indigo-500/20 via-purple-500/15 to-cyan-400/20 px-6 pt-6 pb-16">
            <button onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/50 text-slate-500 hover:text-slate-700">
              <X size={18} />
            </button>
            <h2 className="text-[22px] font-bold text-center text-slate-800 tracking-tight">
              添加品牌监测
            </h2>
            {/* Stepper */}
            <div className="flex items-stretch mt-6 mx-auto max-w-3xl">
              {STEPS.map((s, i) => {
                const done = step > s.k;
                const active = step === s.k;
                return (
                  <div key={s.k} className="flex-1 flex">
                    <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                      active
                        ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                        : done
                        ? "bg-white/70 text-slate-700 border border-white/80"
                        : "bg-white/40 text-slate-400 border border-white/60"
                    }`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0 ${
                        active ? "bg-white text-indigo-600" : done ? "bg-emerald-500 text-white" : "bg-slate-300/60 text-white"
                      }`}>
                        {done ? <Check size={13} /> : s.k}
                      </span>
                      <span className="text-[12.5px] font-semibold whitespace-nowrap">{s.label}</span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="w-0.5 h-auto my-3" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 正文 */}
          <div className="px-8 -mt-8 relative pb-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 min-h-[460px]">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-bold bg-indigo-600 text-white`}>
                  {step}
                </span>
                <h3 className="text-[18px] font-bold text-slate-800">{STEPS[step - 1].label}</h3>
              </div>
              <p className="text-center text-[12.5px] text-slate-500 mb-6">{STEPS[step - 1].desc}</p>

              {step === 1 && (
                <div className="max-w-3xl mx-auto space-y-5">
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">
                      <span className="text-red-500 mr-0.5">*</span>品牌名称
                    </label>
                    <div className="relative">
                      <input value={spec.brandName}
                        onChange={(e) => setSpec({ ...spec, brandName: e.target.value })}
                        placeholder="请输入品牌名，如：爱搜"
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400 pr-[140px]"
                      />
                      <button onClick={doAiFill} disabled={aiFilling || !spec.brandName.trim()}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 px-2.5 py-1.5 rounded-md text-[11px] font-semibold
                                   bg-indigo-50 text-indigo-600 border border-indigo-200
                                   hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
                        <Sparkles size={12} />
                        {aiFilling ? "AI 填写中…" : "使用AI填写URL与品牌介绍"}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">品牌官网URL</label>
                    <input value={spec.websiteUrl}
                      onChange={(e) => setSpec({ ...spec, websiteUrl: e.target.value })}
                      placeholder="请输入品牌地址，如：https://geo.aidso.com/"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">品牌介绍</label>
                    <textarea
                      value={spec.intro}
                      onChange={(e) => setSpec({ ...spec, intro: e.target.value.slice(0, 500) })}
                      rows={7}
                      placeholder="请输入您的服务或品牌描述，越详细越有助于品牌词库与AI归因解析推荐的准确性，如：咖啡 / GEO优化 等"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400 resize-none"
                    />
                    <div className="text-right text-[11px] text-slate-400 mt-1 font-mono">
                      {spec.intro.length}/500
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="max-w-3xl mx-auto space-y-6">
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">
                      品牌词（别名 / 缩写 / 中英文）
                      <Info size={11} className="inline ml-1 text-slate-400" />
                    </label>
                    {tagChips(spec.brandWords, (v) => setSpec({ ...spec, brandWords: v }),
                      "输入后回车添加，如：XEAOS、小舍AI", "#6366F1")}
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">竞品品牌词</label>
                    {tagChips(spec.competitors, (v) => setSpec({ ...spec, competitors: v }),
                      "输入后回车添加，如：Salesforce、用友、钉钉", "#EF4444")}
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">
                      行业标签（用于自动推荐行业问题）
                    </label>
                    {tagChips(spec.tags, (v) => setSpec({ ...spec, tags: v }),
                      "输入后回车添加，如：企业AI操作系统、GEO优化、智慧酒店", "#10B981")}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="max-w-4xl mx-auto space-y-4">
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">
                      推荐问题（勾选后加入监测池）
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-1">
                      {presetQuestions.map((q, i) => {
                        const checked = spec.questions.includes(q);
                        return (
                          <label key={i} className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${
                            checked
                              ? "bg-indigo-50 border-indigo-300"
                              : "bg-white border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30"
                          }`}>
                            <input type="checkbox" className="mt-0.5 accent-indigo-600"
                              checked={checked}
                              onChange={(e) => setSpec({
                                ...spec,
                                questions: e.target.checked
                                  ? [...spec.questions, q]
                                  : spec.questions.filter((x) => x !== q),
                              })}
                            />
                            <span className="text-[12px] text-slate-700 leading-relaxed">{q}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">
                      自定义问题
                      <span className="text-slate-400 ml-1 font-normal">（当前 {spec.questions.length} 个）</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5 mb-2 min-h-[34px]">
                      {spec.questions.filter((q) => !presetQuestions.includes(q)).map((q, i) => (
                        <span key={`c${i}`} className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border border-emerald-200 bg-emerald-50 text-emerald-700">
                          <span className="max-w-[320px] truncate">{q}</span>
                          <button onClick={() => setSpec({ ...spec, questions: spec.questions.filter((x) => x !== q) })}
                            className="opacity-60 hover:opacity-100 ml-1">×</button>
                        </span>
                      ))}
                    </div>
                    <ChipInput placeholder="输入自定义问题后回车添加"
                      color="#10B981"
                      onAdd={(v) => v && !spec.questions.includes(v) && setSpec({ ...spec, questions: [...spec.questions, v] })} />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="max-w-3xl mx-auto space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">监测频率</label>
                      <div className="flex gap-2">
                        {[
                          { k: "hourly", label: "每小时" },
                          { k: "daily", label: "每天" },
                          { k: "weekly", label: "每周" },
                        ].map((o) => (
                          <button key={o.k} onClick={() => setSpec({ ...spec, frequency: o.k as any })}
                            className={`flex-1 px-3 py-2 rounded-lg text-[12px] font-medium transition-all ${
                              spec.frequency === o.k
                                ? "bg-indigo-600 text-white shadow shadow-indigo-300"
                                : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
                            }`}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-700 mb-1.5 font-medium">运行时间</label>
                      <input type="time" value={spec.runAt}
                        onChange={(e) => setSpec({ ...spec, runAt: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[12px] text-slate-700 mb-2 font-medium">
                      勾选 AI 平台
                      <span className="text-slate-400 ml-1 font-normal">（{spec.platforms.length}/{availablePlatforms.length} 已选）</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {availablePlatforms.map((p) => {
                        const on = spec.platforms.includes(p.key);
                        return (
                          <button key={p.key} onClick={() => setSpec({
                            ...spec,
                            platforms: on ? spec.platforms.filter((x) => x !== p.key) : [...spec.platforms, p.key],
                          })}
                            className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all ${
                              on
                                ? "border-indigo-300 bg-indigo-50"
                                : "border-slate-200 bg-white hover:border-indigo-200"
                            }`}>
                            <span className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                              style={{ background: p.color }}>{p.short}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[12px] font-semibold text-slate-700 truncate">{p.name}</div>
                              <div className="text-[10px] text-slate-400">{p.key}</div>
                            </div>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                              on ? "bg-indigo-600 border-indigo-600" : "bg-white border-slate-300"
                            }`}>
                              {on && <Check size={10} className="text-white" strokeWidth={3} />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <label className="flex items-center gap-2 select-none">
                    <input type="checkbox" className="accent-indigo-600 w-4 h-4"
                      checked={spec.enabled}
                      onChange={(e) => setSpec({ ...spec, enabled: e.target.checked })} />
                    <span className="text-[12px] text-slate-600">创建后立即启用监测任务</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* 底部按钮栏 */}
          <div className="px-8 pb-8 flex items-center justify-center gap-3">
            <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
              className="flex items-center gap-1 px-6 py-2.5 rounded-lg text-[13px] font-semibold
                         bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={14} /> 上一步
            </button>
            {step < 4 ? (
              <button onClick={() => setStep(Math.min(4, step + 1))} disabled={!canNext}
                className="flex items-center gap-1 px-10 py-3 rounded-lg text-[14px] font-bold
                           bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-400/30
                           hover:from-indigo-600 hover:to-violet-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none">
                下一步 <ChevronRight size={15} />
              </button>
            ) : (
              <button onClick={submitAll} disabled={!canNext || submitting}
                className="flex items-center gap-1 px-10 py-3 rounded-lg text-[14px] font-bold
                           bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-400/30
                           hover:from-indigo-600 hover:to-violet-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none">
                {submitting ? (
                  <>
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-white/60 border-t-white animate-spin" />
                    保存中…
                  </>
                ) : (
                  <>
                    <Check size={15} /> 完成创建
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/** 小输入框：回车添加标签 chip */
function ChipInput({ placeholder, color, onAdd }: {
  placeholder: string; color: string; onAdd: (text: string) => void;
}) {
  const [v, setV] = useState("");
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          const text = v.trim();
          if (text) { onAdd(text); setV(""); }
        }
      }}
      onBlur={() => {
        const text = v.trim();
        if (text) onAdd(text);
        setV("");
      }}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg border text-[12.5px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:border-transparent"
      style={{
        borderColor: `${color}55`,
        boxShadow: `0 0 0 0 ${color}00`,
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = `0 0 0 3px ${color}18`;
        e.currentTarget.style.borderColor = `${color}99`;
      }}
      onBlurCapture={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = `${color}55`;
      }}
    />
  );
}
